/**
 * Timezone-safe formatter for *calendar-only* date strings.
 *
 * Use this for date-only API fields such as leave `start_date`, `end_date`,
 * and block-date ranges, which the backend stores as a plain `YYYY-MM-DD`
 * string with no time / timezone component.
 *
 * Why a dedicated helper?
 *   `new Date("2026-06-10")` is parsed by JS as **UTC midnight**, which a
 *   browser in a negative-offset zone (e.g. America/New_York, UTC-4 during
 *   DST) then renders as "Jun 9, 2026" — the classic one-day-back shift
 *   reported in the Leave Management module (Jun 2026 RCA).
 *
 *   Routing the same string through `formatDateShortET` would *also* shift
 *   the day, because that helper is built for UTC datetime instants, not
 *   calendar dates.
 *
 * This helper parses the components by hand and constructs a *local* Date,
 * so the rendered day is byte-identical to the stored day in any browser
 * timezone — east, west, or UTC.
 *
 * Lives in its own module (no `date-fns-tz` import) so Jest under
 * `create-react-app` can run unit tests against it without tripping on
 * date-fns's ESM exports.
 *
 * Accepts:
 *   - "YYYY-MM-DD"                (canonical date-only)
 *   - "YYYY-MM-DDTHH:mm[:ss…]"    (datetime — first 10 chars are taken)
 *   - Date instance               (formatted as-is in its local fields)
 *
 * @param {string|Date|null|undefined} dateStr
 * @param {object} [opts]
 * @param {string} [opts.fallback='--'] String returned when input is empty / bad.
 * @returns {string} Formatted as "Jun 10, 2026", or `opts.fallback` otherwise.
 */
export const formatCalendarDate = (dateStr, opts = {}) => {
  const { fallback = '--' } = opts;
  if (!dateStr) return fallback;

  let y, m, d;
  if (dateStr instanceof Date && !isNaN(dateStr.getTime())) {
    y = dateStr.getFullYear();
    m = dateStr.getMonth() + 1;
    d = dateStr.getDate();
  } else {
    const str = String(dateStr).slice(0, 10); // tolerate "YYYY-MM-DDTHH:mm:ss"
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(str);
    if (!match) return fallback;
    [, y, m, d] = match.map(Number);
  }

  // Construct in *local* time. We're only using this object as a vehicle
  // for `toLocaleDateString`'s month-name lookup — no UTC trip involved.
  const local = new Date(y, m - 1, d);
  if (isNaN(local.getTime())) return fallback;

  // eslint-disable-next-line no-restricted-syntax -- DATE-DISPLAY-001: intentional compact "Sep 2, 2026" utility
  return local.toLocaleDateString('en-US', {
    month: 'short',
    day:   'numeric',
    year:  'numeric',
  });
};

/**
 * Parse a date-only string ("YYYY-MM-DD") into a *local-midnight* Date.
 *
 * Companion to {@link formatCalendarDate}. Use whenever you need a real
 * `Date` object for arithmetic or comparison on date-only API fields —
 * e.g. computing leave duration, or checking whether a calendar cell
 * falls inside a leave range. Constructing with `new Date(str)` directly
 * parses as UTC midnight and silently invites the "−1 day in ET" class of
 * bugs even when the math happens to come out right (offsets cancel in
 * subtraction). This helper keeps everything in wall-clock space.
 *
 * @param {string|Date|null|undefined} dateStr
 * @returns {Date|null} Date pinned to local midnight, or null on bad input.
 */
export const parseCalendarDate = (dateStr) => {
  if (!dateStr) return null;
  if (dateStr instanceof Date) {
    return isNaN(dateStr.getTime()) ? null : dateStr;
  }
  const str = String(dateStr).slice(0, 10);
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(str);
  if (!match) return null;
  const [, y, m, d] = match.map(Number);
  const local = new Date(y, m - 1, d);
  return isNaN(local.getTime()) ? null : local;
};

export default formatCalendarDate;